import { IExercise } from '../../../types/interfaces/exercise';
import { IExerciseOption } from '../../../types/interfaces/exercise-option';
import {
  IAvailableDetailedExerciseResponse,
  IAvailableExerciseResponse,
} from './interfaces';

export function buildExerciseOptionsFromRefs(
  exerciseOptionItems: IAvailableExerciseResponse['exerciseOptionItems'],
  exerciseOptions: IExerciseOption[],
): IExerciseOption[] {
  if (!exerciseOptionItems?.length || !exerciseOptions.length) {
    return [];
  }

  const byId = new Map(
    exerciseOptions.map((option) => [option.id, option] as const),
  );

  return exerciseOptionItems
    .map((item) => byId.get(item.optionId))
    .filter((option): option is IExerciseOption => option != null);
}

export function mapAvailableExerciseResponseToExercise(
  raw: IAvailableExerciseResponse,
  exerciseOptions: IExerciseOption[],
): IExercise {
  const existing = raw as unknown as IExercise;
  if (existing.options?.length) {
    return existing;
  }

  const options = buildExerciseOptionsFromRefs(
    raw.exerciseOptionItems,
    exerciseOptions,
  );

  return {
    ...(raw as object),
    options,
  } as IExercise;
}

export function mapAvailableDetailedExerciseResponseToExercise(
  raw: IAvailableDetailedExerciseResponse,
  exerciseOptions: IExerciseOption[],
): IExercise {
  const base = mapAvailableExerciseResponseToExercise(raw, exerciseOptions);

  return {
    ...base,
    mainMuscleGroup: raw.mainMuscleGroup,
    mainMuscleGroupId: raw.mainMuscleGroup?.id,
    additionalMuscles: raw.additionalMuscles,
    developedSkills: raw.developedSkills,
  } as IExercise;
}

export function exerciseListNeedsOptionResolution(
  exercises: IExercise[] | undefined,
): boolean {
  return (
    exercises?.some(
      (ex) => !ex.options?.length && !!ex.exerciseOptionItems?.length,
    ) ?? false
  );
}

export function normalizeExerciseWithOptionRefs(
  exercise: IExercise,
  exerciseOptions: IExerciseOption[],
): IExercise {
  if (exercise.options?.length) {
    return exercise;
  }

  const options = buildExerciseOptionsFromRefs(
    exercise.exerciseOptionItems,
    exerciseOptions,
  );

  return {
    ...exercise,
    options,
  };
}
